import { useQuery } from "@tanstack/react-query";
import {
  getTimeline,
  getWorkflowConfig,
  getWorkflowFacets,
  type WorkflowConfig,
  type WorkflowFacet,
  type WorkflowState,
} from "../api/workflow";

const ESS_WORKFLOW = "ess_suggestion";
const ESS_ENTITY_TYPE = "ess_suggestion";

export function useWorkflowConfig(workflowName: string = ESS_WORKFLOW) {
  return useQuery({
    queryKey: ["workflow-config", workflowName],
    queryFn: () => getWorkflowConfig(workflowName),
    // Workflow shape is defined in yaml at deploy time. It does not change
    // during a session, so cache aggressively.
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function useWorkflowFacets(workflowName: string = ESS_WORKFLOW) {
  return useQuery({
    queryKey: ["workflow-facets", workflowName],
    queryFn: () => getWorkflowFacets(workflowName),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTimeline(
  entityId: string | undefined,
  workflowName: string = ESS_WORKFLOW,
  entityType: string = ESS_ENTITY_TYPE,
) {
  return useQuery({
    queryKey: ["workflow-timeline", workflowName, entityType, entityId],
    queryFn: () => getTimeline(workflowName, entityType, entityId as string),
    enabled: !!entityId,
  });
}

export function lookupState(
  config: WorkflowConfig | undefined,
  state: string,
): WorkflowState | undefined {
  return config?.states?.[state];
}

// snake_case → "Title Case" — used as the fallback when the runtime
// workflow yaml has no label entry for a given state. Without this
// rejected_by_wc, accepted_by_hr, etc. render raw on chips and
// breadcrumbs whenever the workflow config drifts from what the data
// contains (e.g. legacy rows after a status retirement).
// Sentence case, like the yaml's own labels ("Under WC review" rather than
// "Under Wc Review"). `sc` / `ssc` are the Steering Committee (SC).
const ACRONYMS: Record<string, string> = {
  wc: "WC",
  sc: "SC",
  ssc: "SC",
  hod: "HOD",
  hof: "HOF",
  hsef: "HSEF",
  hr: "HR",
  pp: "PP",
  pe: "PE",
};
export function humanizeStatus(state: string): string {
  return state
    .split("_")
    .filter(Boolean)
    .map((w, i) => {
      const acronym = ACRONYMS[w.toLowerCase()];
      if (acronym) return acronym;
      const lower = w.toLowerCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(" ");
}

export function statusLabel(
  config: WorkflowConfig | undefined,
  state: string | null | undefined,
): string {
  if (!state) return "";
  return lookupState(config, state)?.label ?? humanizeStatus(state);
}

export function statusShortLabel(
  config: WorkflowConfig | undefined,
  state: string | null | undefined,
): string {
  if (!state) return "";
  const s = lookupState(config, state);
  return s?.short_label ?? s?.label ?? humanizeStatus(state);
}

export function statusCategory(
  config: WorkflowConfig | undefined,
  state: string | null | undefined,
): string {
  if (!state) return "submitted";
  return lookupState(config, state)?.category ?? "submitted";
}

export function findFacet(
  facets: WorkflowFacet[] | undefined,
  facetName: string,
): WorkflowFacet | undefined {
  return facets?.find((f) => f.name === facetName);
}
